/**
 * Logout — emergency sign-out route.
 *
 * Reachable via `/logout` in the address bar. Useful when the Sign-out
 * button on the Navbar is clipped off-screen on narrow viewports / wide
 * locale strings, or when an SSO-redirected user wants a deterministic
 * way to drop their session. Always navigable, even when the user is
 * already signed out (acts as a no-op + bounce to /login).
 */
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";

export default function Logout() {
  const { logout, user } = useAuth();

  useEffect(() => {
    if (user) {
      // `logout()` itself handles the redirect for SSO users
      // (full RP-initiated logout) and clears local storage for
      // password/magic-link users.
      logout();
      // Belt + braces — if `logout()` doesn't trigger a navigation
      // (e.g. non-SSO path), bounce manually after the state update.
      setTimeout(() => {
        if (window.location.pathname === "/logout") {
          window.location.replace("/login?signed_out=1");
        }
      }, 50);
    } else {
      window.location.replace("/login?signed_out=1");
    }
  }, [logout, user]);

  return (
    <div
      data-testid="logout-page"
      className="flex min-h-screen items-center justify-center bg-ek-ink text-white/70"
    >
      <p className="text-sm font-light tracking-wide">Signing out…</p>
    </div>
  );
}
